import React, { useContext, useCallback } from 'react';
import { IViewDishContext, ViewDishContext } from '../../viewDishState/viewDishContext';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { ModalAssignDishToRestaurants } from 'components/ModalWindows/ModalAssignDishToRestaurants';
import { SuggestedModal } from './SuggestedModal';
import { AdditionalModal } from './AdditionalModal';
import { ModificationsModal } from './ModificationsModal';
import { ModalAssignDishToSubcategoryOnRestaurant } from './ModalAssignDishToSubcategoryOnRestaurant';
import { ModalEditModGroupTemplate } from 'components/ModalWindows/ModalEditModGroupTemplate';
import { CreatePortionModal } from './CreatePortionModal';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import { EditPortionModal } from './EditPortionModal';
import { ViewPortionModal } from './ViewPortionModal';

export const ModalWindows: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		refetchData,
		refetchRestPropsData,
		refetchRestaurantPortionsData,
		deleteSelectedPortion,
		dishFullInfo,
		restaurantIdForRestaurantProps,
		typeOfModalWindow,
	} = useContext<IViewDishContext>(ViewDishContext);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, [changeTypeOfModalWindow]);

	const handleCloseModalWithRefetchData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, [refetchData, changeTypeOfModalWindow]);

	const handleCloseModalWithError = useCallback(() => {
		changeTypeOfModalWindow('errorUpdatingDish');
	}, [changeTypeOfModalWindow]);

	const handleCloseDishHasPortionsError = useCallback(() => {
		changeTypeOfModalWindow('errorDishHasPortions');
	}, [changeTypeOfModalWindow]);

	const handleCloseModalWithRefetchRestPropsData = useCallback(() => {
		refetchRestPropsData(true);
		changeTypeOfModalWindow(null);
	}, [refetchRestPropsData, changeTypeOfModalWindow]);

	const handleCloseModalWithRefetchPortions = useCallback(() => {
		refetchData(true);
		refetchRestPropsData(true);
		refetchRestaurantPortionsData(true);
		changeTypeOfModalWindow(null);
	}, [refetchData, refetchRestPropsData, refetchRestaurantPortionsData, changeTypeOfModalWindow]);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow}>
			{typeOfModalWindow === 'errorUpdatingDish' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorUpdatingDish" />
			)}
			{typeOfModalWindow === 'errorDishHasPortions' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorDishHasPortions" />
			)}
			{typeOfModalWindow === 'assignRestaurants' && (
				<ModalAssignDishToRestaurants
					onCloseModal={handleCloseModalWithRefetchData}
					onFailureEvent={handleCloseModal}
					onShowError={handleCloseModalWithError}
					dishId={dishFullInfo?.id || ''}
				/>
			)}
			{typeOfModalWindow === 'assignSuggested' && (
				<SuggestedModal
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchData}
					onShowError={handleCloseModalWithError}
				/>
			)}
			{typeOfModalWindow === 'assignAdditional' && (
				<AdditionalModal
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchData}
					onShowError={handleCloseModalWithError}
				/>
			)}
			{typeOfModalWindow === 'assignModifications' && (
				<ModificationsModal
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchData}
					onShowError={handleCloseModalWithError}
				/>
			)}
			{typeOfModalWindow === 'assignSubcategories' && restaurantIdForRestaurantProps && (
				<ModalAssignDishToSubcategoryOnRestaurant
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchRestPropsData}
					onShowError={handleCloseModalWithError}
					dishId={dishFullInfo?.id || ''}
					IDSelectedRestaurant={restaurantIdForRestaurantProps}
				/>
			)}
			{typeOfModalWindow === 'editModGroupTemplate' && (
				<ModalEditModGroupTemplate
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchRestPropsData}
					onShowError={handleCloseModalWithError}
					onShowProductHasPortionsError={handleCloseDishHasPortionsError}
					dishId={dishFullInfo?.id || ''}
				/>
			)}
			{typeOfModalWindow === 'createPortion' && (
				<CreatePortionModal
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchPortions}
					onShowError={handleCloseModalWithError}
				/>
			)}
			{typeOfModalWindow === 'editPortion' && (
				<EditPortionModal
					onFailureEvent={handleCloseModal}
					onCloseModal={handleCloseModalWithRefetchPortions}
					onShowError={handleCloseModalWithError}
				/>
			)}
			{typeOfModalWindow === 'viewPortion' && <ViewPortionModal onCloseModal={handleCloseModal} />}
			{typeOfModalWindow === 'toConfirmDeletePortion' && (
				<ModalToConfirm
					onConsentEvent={deleteSelectedPortion}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="deletePortion"
					typeOfInfoIcon="toDelete"
				/>
			)}
			{typeOfModalWindow === 'successDeletingPortion' && (
				<ModalSentSuccessfully
					onConsentEvent={handleCloseModal}
					isVisible={true}
					description="deletePortionSuccessfully"
				/>
			)}
			{typeOfModalWindow === 'errorDeletingPortion' && (
				<ModalSentError
					onConsentEvent={handleCloseModal}
					isVisible={true}
					description="errorWhileDeletingPortion"
				/>
			)}
		</ModalWrapper>
	);
};
